import React, { useEffect, useState, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { format, addDays, startOfWeek, endOfWeek, parseISO } from "date-fns";
import { MapPin, ChevronLeft, ChevronRight, Layers, Route, X, Loader2, LocateFixed, Calendar, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/common/SearchBar";
import { PillRail, Pill } from "@/components/common/Segmented";
import { DateJump } from "@/components/common/DateJump";
import { btn } from "@/components/common/btn";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getMapData, getTechnicians, getSmartRoute, geocodeAddress } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const STATUS_COLORS = {
  approved: "#3b82f6",
  scheduled: "#3b82f6",
  en_route: "#8b5cf6",
  on_site: "#f59e0b",
};

const STATUS_LABELS = {
  approved: "Approved", scheduled: "Scheduled",
  en_route: "En Route", on_site: "On Site",
};

function makeIcon(color, size = 28) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size + 10}" viewBox="0 0 ${size} ${size + 10}">
    <path d="${`M${size / 2} ${size + 8} C${size / 2} ${size + 8} ${size - 2} ${size / 2 + 4} ${size - 2} ${size / 2 - 2} A${size / 2 - 2} ${size / 2 - 2} 0 1 0 2 ${size / 2 - 2} C2 ${size / 2 + 4} ${size / 2} ${size + 8} ${size / 2} ${size + 8}Z`}" fill="${color}" stroke="#fff" stroke-width="2"/>
    <circle cx="${size / 2}" cy="${size / 2 - 2}" r="${size / 5}" fill="#fff"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [size, size + 10], iconAnchor: [size / 2, size + 10], popupAnchor: [0, -(size + 4)],
  });
}

function makeNumberedIcon(color, num) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
    <path d="M16 40 C16 40 30 22 30 14 A14 14 0 1 0 2 14 C2 22 16 40 16 40Z" fill="${color}" stroke="#fff" stroke-width="2"/>
    <text x="16" y="17" font-size="12" font-weight="bold" fill="#fff" text-anchor="middle" dominant-baseline="middle">${num}</text>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [32, 42], iconAnchor: [16, 42], popupAnchor: [0, -36],
  });
}

function makeTechIcon(color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="14" fill="${color}" stroke="#fff" stroke-width="3"/>
    <circle cx="16" cy="12" r="4" fill="#fff"/>
    <path d="M8 24c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="#fff" opacity="0.7"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [32, 32], iconAnchor: [16, 16], popupAnchor: [0, -20],
  });
}

// Hollow ring icon for scheduled-but-not-pinged techs. Sits ON the site
// they're assigned to so the dispatcher can see "where they SHOULD be".
function makeTechGhostIcon(color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="12" fill="none" stroke="${color}" stroke-width="3" stroke-dasharray="3 2" opacity="0.85"/>
    <circle cx="16" cy="16" r="3" fill="${color}" opacity="0.55"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [32, 32], iconAnchor: [16, 16], popupAnchor: [0, -20],
  });
}

// Faded tech icon for stale last-known-GPS (ghost at site + faded pin at last GPS).
function makeTechStaleIcon(color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 32 32" opacity="0.45">
    <circle cx="16" cy="16" r="13" fill="${color}" stroke="#fff" stroke-width="2.5"/>
    <circle cx="16" cy="12" r="4" fill="#fff"/>
    <path d="M8 24c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="#fff" opacity="0.7"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -18],
  });
}

// Outline ring + house = home address pin — green border, tech color interior
function makeHomePinIcon(color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
    <circle cx="15" cy="15" r="12" fill="white" stroke="#22c55e" stroke-width="2.5"/>
    <polygon points="15,7 22,13 20,13 20,21 17,21 17,17 13,17 13,21 10,21 10,13 8,13" fill="${color}"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -18],
  });
}

// Teardrop + pulse ring = live GPS pin — green border, tech color fill
function makeLiveGpsPinIcon(color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="42" height="52" viewBox="0 0 42 52">
    <circle cx="21" cy="21" r="18" fill="#22c55e" opacity="0.15">
      <animate attributeName="r" values="12;20;12" dur="2s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values="0.3;0;0.3" dur="2s" repeatCount="indefinite"/>
    </circle>
    <path d="M21 50 C21 50 36 30 36 18 A15 15 0 1 0 6 18 C6 30 21 50 21 50Z" fill="${color}" stroke="#22c55e" stroke-width="2.5"/>
    <circle cx="21" cy="18" r="6" fill="#fff"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [42, 52], iconAnchor: [21, 50], popupAnchor: [0, -44],
  });
}

// Black teardrop — Work Orders
function makeWOIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
    <path d="M16 40 C16 40 30 22 30 14 A14 14 0 1 0 2 14 C2 22 16 40 16 40Z" fill="#f97316" stroke="#fff" stroke-width="2"/>
    <circle cx="16" cy="14" r="5" fill="#fff"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [32, 42], iconAnchor: [16, 42], popupAnchor: [0, -36],
  });
}

// Contract job pin — same teardrop with a blue ring + CONTRACT tag (service pins are untagged dark teardrops)
function makeContractIcon(cj = {}) {
  const color = cj.color || "#3B82F6";
  const tag = cj.tag || "CON";
  const fg = cj.job_kind === "overhead" ? "#0f172a" : "#fff";
  const html = `<div style="position:relative;width:64px;height:54px;display:flex;flex-direction:column;align-items:center" data-testid="map-pin-${cj.job_kind || "contract"}">
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
      <path d="M16 40 C16 40 30 22 30 14 A14 14 0 1 0 2 14 C2 22 16 40 16 40Z" fill="#f97316" stroke="${color}" stroke-width="3"/>
      <circle cx="16" cy="14" r="5" fill="${color}"/>
    </svg>
    <span style="position:absolute;top:-10px;background:${color};color:${fg};font:900 7px/1 system-ui;letter-spacing:.04em;padding:2px 4px;border-radius:3px;border:1px solid #fff">${tag}</span>
  </div>`;
  return L.divIcon({ html, className: "", iconSize: [64, 54], iconAnchor: [32, 52], popupAnchor: [0, -46] });
}

// Purple diamond — Customer
function makeCustomerIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
    <rect x="3" y="3" width="16" height="16" rx="3" fill="#8b5cf6" stroke="#fff" stroke-width="2.5" transform="rotate(45 11 11)"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [22, 22], iconAnchor: [11, 11], popupAnchor: [0, -14],
  });
}

function makeStartIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
    <circle cx="14" cy="14" r="12" fill="#059669" stroke="#fff" stroke-width="3"/>
    <polygon points="11,8 22,14 11,20" fill="#fff"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -18],
  });
}

function makeInspectionIcon(color = "#f97316", isOverdue = false) {
  const fill = isOverdue ? "#ef4444" : color;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
    <path d="M15 38 C15 38 28 22 28 14 A13 13 0 1 0 2 14 C2 22 15 38 15 38Z" fill="${fill}" stroke="#fff" stroke-width="2"/>
    <rect x="9" y="7" width="12" height="14" rx="2" fill="#fff" opacity="0.9"/>
    <line x1="12" y1="11" x2="18" y2="11" stroke="${fill}" stroke-width="1.5"/>
    <line x1="12" y1="14" x2="18" y2="14" stroke="${fill}" stroke-width="1.5"/>
    <line x1="12" y1="17" x2="16" y2="17" stroke="${fill}" stroke-width="1.5"/>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [30, 40], iconAnchor: [15, 40], popupAnchor: [0, -34],
  });
}

function makeDualIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
    <defs>
      <clipPath id="left-half"><rect x="0" y="0" width="15" height="40"/></clipPath>
      <clipPath id="right-half"><rect x="15" y="0" width="15" height="40"/></clipPath>
    </defs>
    <path d="M15 38 C15 38 28 22 28 14 A13 13 0 1 0 2 14 C2 22 15 38 15 38Z" fill="#3b82f6" clip-path="url(#left-half)"/>
    <path d="M15 38 C15 38 28 22 28 14 A13 13 0 1 0 2 14 C2 22 15 38 15 38Z" fill="#f97316" clip-path="url(#right-half)"/>
    <path d="M15 38 C15 38 28 22 28 14 A13 13 0 1 0 2 14 C2 22 15 38 15 38Z" fill="none" stroke="#fff" stroke-width="2"/>
    <circle cx="15" cy="13" r="6" fill="#fff" opacity="0.9"/>
    <text x="15" y="16" text-anchor="middle" font-size="9" font-weight="bold" fill="#1e293b" font-family="sans-serif">W+I</text>
  </svg>`;
  return L.divIcon({
    html: svg, className: "",
    iconSize: [30, 40], iconAnchor: [15, 40], popupAnchor: [0, -34],
  });
}

function FitBounds({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
  }, [markers, map]);
  return null;
}

const START_LOCATIONS = {
  shop: { label: "Shop (ABS)", address: "1785 W. 160th Ave., Broomfield, CO", lat: 39.9534, lng: -105.0266 },
  abs_cs: { label: "ABS-CS", address: "920 Elkton Dr, Colorado Springs, CO", lat: 38.8339, lng: -104.8214 },
};

const DAY_COLORS = ["#059669", "#3b82f6", "#8b5cf6", "#f59e0b", "#ef4444", "#ec4899", "#14b8a6"];

export default function MapView() {
  // Tech role users get a privacy-scoped map: peer tech pins only when
  // they're actively on a job (en_route / at_site / at_shop) — never
  // their home address pins or idle GPS pings.
  const { user } = useAuth();
  const isTechView = user?.role === "technician";
  const [mapData, setMapData] = useState({ work_orders: [], customers: [], technicians: [], scheduled_techs: [], tech_home_pins: [] });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = startOfWeek(today, { weekStartsOn: 1 });
    return { start: format(start, "yyyy-MM-dd"), end: format(endOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd") };
  });
  // #164 (Group H): inspections layer is referenced throughout the
  // map (filteredInspections, inspectionWoIds, fit-bounds) but was
  // missing from layer state — toggling it via the UI did nothing. Add
  // it here with a sensible default so the toggle button below actually
  // controls inspection pins.
  // Technicians land on their own scheduled work for the week; office users see every WO + tech pin.
  const [layers, setLayers] = useState({ work_orders: true, customers: false, technicians: !isTechView });
  const [statusFilter, setStatusFilter] = useState(isTechView ? "scheduled" : "all");
  const [techFilter, setTechFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [allTechs, setAllTechs] = useState([]);

  useEffect(() => {
    if (!isTechView || !allTechs.length) return;
    const me = allTechs.find(t => t.user_id === user?.id || t.id === user?.id || (t.email && t.email === user?.email));
    if (me) setTechFilter(me.id);
  }, [isTechView, allTechs, user]);
  // Smart Route
  const [routeMode, setRouteMode] = useState(false);
  const [routeTechId, setRouteTechId] = useState("");
  const [routeStartOption, setRouteStartOption] = useState("shop"); // shop | abs_cs | tech_home | custom
  const [routeCustomAddress, setRouteCustomAddress] = useState("");
  const [routeData, setRouteData] = useState(null); // { days: [...], total_distance_km, total_duration_min }
  const [routeLoading, setRouteLoading] = useState(false);
  const [selectedRouteDay, setSelectedRouteDay] = useState("all"); // "all" or date string

  useEffect(() => { loadData(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [dateRange]);
  useEffect(() => { getTechnicians().then(r => setAllTechs(r.data.filter(t => !t.is_archived))).catch(() => {}); }, []);

  const loadData = async () => {
    try {
      const res = await getMapData({ start_date: dateRange.start, end_date: dateRange.end });
      setMapData(res.data);
    } catch { toast.error("Failed to load map data"); }
    finally { setLoading(false); }
  };

  const shiftRange = (days) => {
    setDateRange(prev => ({
      start: format(addDays(new Date(prev.start + "T12:00:00"), days), "yyyy-MM-dd"),
      end: format(addDays(new Date(prev.end + "T12:00:00"), days), "yyyy-MM-dd"),
    }));
  };
  // Any picked day → the Mon–Sun week that contains it (pins = WOs scheduled that week).
  const pickWeekOf = (d) => setDateRange({ start: format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd"), end: format(endOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd") });
  const goThisWeek = () => pickWeekOf(new Date());

  const filteredWOs = useMemo(() => {
    let wos = mapData.work_orders || [];
    if (statusFilter !== "all") wos = wos.filter(w => w.status === statusFilter);
    if (techFilter !== "all") wos = wos.filter(w => (w.assigned_tech_ids || []).includes(techFilter));
    return wos;
  }, [mapData.work_orders, statusFilter, techFilter]);

  // Global map search: anything on any pin. Matches stay solid, everything else fades, and the map zooms to the matches.
  const q = search.trim().toLowerCase();
  const has = useCallback((...vals) => !q || vals.some(v => (v == null ? "" : String(v)).toLowerCase().includes(q)), [q]);
  const woHit = useCallback((w) => has(w.wo_number, w.title, w.client_name, w.site_name, w.site_address, STATUS_LABELS[w.status],
    ...(w.assigned_tech_ids || []).map(id => allTechs.find(t => t.id === id)?.name)), [has, allTechs]);
  const cjHit = useCallback((cj) => has(cj.title, cj.client_name, cj.wo_number, cj.site_name, cj.site_address, cj.tag), [has]);
  const custHit = useCallback((c) => has(c.client_name, c.site_name, c.city), [has]);
  const techHit = useCallback((t) => has(t.name, t.address), [has]);
  const dim = (hit) => (q && !hit ? 0.18 : 1);

  // Compute overlap: WOs that also have open inspections (same wo_id)
  const inspectionsByWoId = useMemo(() => {
    const map = {};
    for (const insp of (mapData.inspections || [])) {
      if (!map[insp.wo_id]) map[insp.wo_id] = [];
      map[insp.wo_id].push(insp);
    }
    return map;
  }, [mapData.inspections]);

  // WO IDs that are visible on the map (wo pin uses "id" field)
  const visibleWoIds = useMemo(() => new Set(filteredWOs.map(w => w.id)), [filteredWOs]);


  // WO IDs that have inspections (for dual icon)
  const wosWithInspections = useMemo(() => {
    const ids = new Set();
    for (const insp of (mapData.inspections || [])) {
      if (visibleWoIds.has(insp.wo_id)) ids.add(insp.wo_id);
    }
    return ids;
  }, [mapData.inspections, visibleWoIds]);

  // Compute visible route for selected day
  const visibleRoute = useMemo(() => {
    if (!routeData?.days) return null;
    if (selectedRouteDay === "all") {
      // Merge all days — deduplicate start, add day labels
      const allWaypoints = [];
      let startAdded = false;
      for (const day of routeData.days) {
        const dayLabel = format(parseISO(day.date), "EEE");
        for (const wp of (day.waypoints || [])) {
          if (wp.type === "start") {
            if (!startAdded) { allWaypoints.push(wp); startAdded = true; }
          } else {
            allWaypoints.push({ ...wp, day_label: dayLabel });
          }
        }
      }
      return { waypoints: allWaypoints, route_geometries: routeData.days.map(d => d.route_geometry || []), total_distance_km: routeData.total_distance_km, total_duration_min: routeData.total_duration_min };
    }
    const day = routeData.days.find(d => d.date === selectedRouteDay);
    if (!day) return null;
    return { waypoints: day.waypoints || [], route_geometries: [day.route_geometry || []], total_distance_km: day.total_distance_km, total_duration_min: day.total_duration_min };
  }, [routeData, selectedRouteDay]);

  const allMarkers = useMemo(() => {
    const pts = [];
    if (layers.work_orders) filteredWOs.filter(woHit).forEach(w => pts.push({ lat: w.lat, lng: w.lng }));
    if (layers.work_orders) (mapData.contract_jobs || []).filter(cjHit).forEach(c => pts.push({ lat: c.lat, lng: c.lng }));
    if (layers.customers) (mapData.customers || []).filter(custHit).forEach(c => pts.push({ lat: c.lat, lng: c.lng }));
    if (layers.technicians) (mapData.technicians || []).filter(techHit).forEach(t => pts.push({ lat: t.lat, lng: t.lng }));
    if (layers.technicians) (mapData.tech_home_pins || []).filter(techHit).forEach(t => pts.push({ lat: t.lat, lng: t.lng }));
    if (visibleRoute?.waypoints && !q) visibleRoute.waypoints.forEach(w => pts.push({ lat: w.lat, lng: w.lng }));
    return pts.filter(p => p.lat && p.lng);
  }, [filteredWOs, mapData.contract_jobs, mapData.customers, mapData.technicians, mapData.tech_home_pins, layers, visibleRoute, q, woHit, cjHit, custHit, techHit]);

  const handleCalcRoute = async () => {
    if (!routeTechId) { toast.error("Select a technician"); return; }

    // Resolve start coordinates based on selected option
    let startLat, startLng;
    if (routeStartOption === "shop") {
      startLat = START_LOCATIONS.shop.lat;
      startLng = START_LOCATIONS.shop.lng;
    } else if (routeStartOption === "abs_cs") {
      startLat = START_LOCATIONS.abs_cs.lat;
      startLng = START_LOCATIONS.abs_cs.lng;
    } else if (routeStartOption === "tech_home") {
      const tech = allTechs.find(t => t.id === routeTechId);
      // Prefer live GPS position if available, fall back to home address
      const liveTech = (mapData.technicians || []).find(t => t.id === routeTechId);
      if (liveTech?.lat && liveTech?.lng) {
        startLat = liveTech.lat;
        startLng = liveTech.lng;
        toast.info("Using live GPS position as start point");
      } else if (tech?.home_lat) {
        startLat = tech.home_lat;
        startLng = tech.home_lng;
      } else {
        toast.error("Tech has no home address — add one in Settings > Technicians");
        return;
      }
    } else if (routeStartOption === "custom") {
      if (!routeCustomAddress.trim()) { toast.error("Enter a custom address"); return; }
      try {
        const geo = await geocodeAddress({ address: routeCustomAddress });
        startLat = geo.data.lat;
        startLng = geo.data.lng;
      } catch {
        toast.error("Could not find that address — try a more specific one");
        return;
      }
    }

    setRouteLoading(true);
    try {
      const res = await getSmartRoute({
        tech_id: routeTechId,
        start_date: dateRange.start,
        end_date: dateRange.end,
        start_lat: startLat,
        start_lng: startLng,
      });
      const data = res.data;
      if (!data.days || data.days.length === 0) {
        toast.info("No routable WOs found for this tech in this date range");
        setRouteData(null);
      } else {
        setRouteData(data);
        setSelectedRouteDay(data.days.length === 1 ? data.days[0].date : "all");
        const totalStops = data.days.reduce((s, d) => s + d.stop_count, 0);
        toast.success(`Route: ${data.days.length} day${data.days.length > 1 ? "s" : ""}, ${totalStops} stops, ${data.total_distance_km} km`);
      }
    } catch { toast.error("Failed to calculate route"); }
    finally { setRouteLoading(false); }
  };

  const clearRoute = () => { setRouteData(null); setRouteMode(false); setRouteTechId(""); setSelectedRouteDay("all"); };

  const defaultCenter = [39.7392, -104.9903];
  const hasMarkers = allMarkers.length > 0;

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900" /></div>;

  return (
    <div className="flex flex-col gap-2" data-testid="map-view-page">
      {/* Controls — bare toolbar row, same as Dispatch / Timesheets */}
      <div className="shrink-0 flex flex-col gap-2" style={{ position: "relative", zIndex: 10 }}>
        <div className="flex flex-wrap items-center gap-2" data-testid="map-toolbar">
          <div className="flex items-center gap-2 flex-wrap flex-1 justify-between">
            <SearchBar value={search} onChange={setSearch} placeholder="Search the map..." width="w-44" testId="map-search" />
            <PillRail testId="map-layer-pills">
              {[
                { key: "work_orders", label: "Work Orders", color: "#f97316" },
                { key: "customers", label: "Customers", color: "#8b5cf6" },
                { key: "technicians", label: "Techs", color: "#22c55e" },
              ].map(l => (
                <Pill key={l.key} on={layers[l.key]} onStyle={{ backgroundColor: l.color, color: "#fff" }} onClick={() => setLayers(prev => ({ ...prev, [l.key]: !prev[l.key] }))} testId={`toggle-layer-${l.key}`}
                  title={layers[l.key] ? `Hide ${l.label.toLowerCase()}` : `Show ${l.label.toLowerCase()}`}>{l.label}</Pill>
              ))}
            </PillRail>
            {!routeMode && (
              <button type="button" className={btn("mode")} onClick={() => setRouteMode(true)} data-testid="btn-smart-route">
                <Route className="w-3.5 h-3.5" />Smart Route
              </button>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-7 w-[108px] text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Statuses</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={techFilter} onValueChange={setTechFilter}>
              <SelectTrigger className="h-7 w-[120px] text-xs" data-testid="filter-tech"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Techs</SelectItem>
                {allTechs.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="h-4 w-px bg-slate-200" />
            <div className="ml-auto flex items-center gap-2 shrink-0" style={{ "--datejump-minw": "0px" }}>
            <button type="button" className={btn("nav")} onClick={() => shiftRange(-7)} data-testid="btn-map-prev" title="Previous week"><ChevronLeft className="w-3.5 h-3.5" /></button>
            <button type="button" className={btn("nav")} onClick={goThisWeek} data-testid="btn-map-today">Today</button>
            <button type="button" className={btn("nav")} onClick={() => shiftRange(7)} data-testid="btn-map-next" title="Next week"><ChevronRight className="w-3.5 h-3.5" /></button>
            <DateJump testId="map-date-jump" value={new Date(dateRange.start + "T12:00:00")} onPick={pickWeekOf}
              label={`${format(new Date(dateRange.start + "T12:00:00"), "MMM d")} – ${format(new Date(dateRange.end + "T12:00:00"), "MMM d, yy")}`} />
            </div>
          </div>
        </div>

        {/* Smart Route */}
        {routeMode && (
        <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 flex-wrap" data-testid="smart-route-panel">
              <Route className="w-4 h-4 text-emerald-600 shrink-0" />
              <Select value={routeTechId} onValueChange={setRouteTechId}>
                <SelectTrigger className="h-7 w-[130px] text-xs bg-white" data-testid="route-tech-select"><SelectValue placeholder="Tech..." /></SelectTrigger>
                <SelectContent>
                  {allTechs.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={routeStartOption} onValueChange={setRouteStartOption}>
                <SelectTrigger className="h-7 w-[140px] text-xs bg-white" data-testid="route-start-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shop">Shop (ABS)</SelectItem>
                  <SelectItem value="abs_cs">ABS-CS</SelectItem>
                  <SelectItem value="tech_home">Tech Home</SelectItem>
                  <SelectItem value="custom">Custom Address</SelectItem>
                </SelectContent>
              </Select>
              {routeStartOption === "custom" ? (
                <Input value={routeCustomAddress} onChange={(e) => setRouteCustomAddress(e.target.value)}
                  className="h-7 text-xs w-[200px] bg-white" placeholder="Enter address..." data-testid="route-custom-address" />
              ) : (
                <span className="text-[10px] text-emerald-600 max-w-[180px] truncate">
                  {routeStartOption === "shop" && START_LOCATIONS.shop.address}
                  {routeStartOption === "abs_cs" && START_LOCATIONS.abs_cs.address}
                  {routeStartOption === "tech_home" && (allTechs.find(t => t.id === routeTechId)?.address || "No address set")}
                </span>
              )}
              <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleCalcRoute} disabled={routeLoading} data-testid="btn-calc-route">
                {routeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5 mr-1" />}
                {routeLoading ? "..." : "Route"}
              </Button>
              <button onClick={clearRoute} className="text-slate-400 hover:text-slate-600" data-testid="btn-clear-route"><X className="w-4 h-4" /></button>
            </div>
        </div>
        )}


        {/* Route Summary with Day Tabs */}
        {routeData && routeData.days?.length > 0 && (
          <div className="space-y-2" data-testid="route-summary">
            {/* Day selector tabs */}
            <div className="flex items-center gap-2 flex-wrap">
              <Calendar className="w-4 h-4 text-emerald-600 shrink-0" />
              <button
                onClick={() => setSelectedRouteDay("all")}
                className={`text-xs px-3 py-1 rounded-full border transition-all font-medium ${selectedRouteDay === "all" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}
                data-testid="route-day-all"
              >
                All ({routeData.days.reduce((s, d) => s + d.stop_count, 0)} stops)
              </button>
              {routeData.days.map(day => (
                <button
                  key={day.date}
                  onClick={() => setSelectedRouteDay(day.date)}
                  className={`text-xs px-3 py-1 rounded-full border transition-all font-medium ${selectedRouteDay === day.date ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}
                  data-testid={`route-day-${day.date}`}
                >
                  {format(parseISO(day.date), "EEE M/d")} ({day.stop_count})
                </button>
              ))}
              <span className="ml-auto text-xs text-slate-400 font-medium">
                {visibleRoute ? `${visibleRoute.total_distance_km} km | ~${visibleRoute.total_duration_min} min` : ""}
              </span>
            </div>

            {/* Waypoint pills for selected day */}
            {visibleRoute?.waypoints?.length > 0 && (
              <div className="flex items-center gap-1 overflow-x-auto pb-1">
                {visibleRoute.waypoints.map((wp, i) => {
                  const stopNum = wp.type === "start" ? null : visibleRoute.waypoints.slice(0, i).filter(w => w.type !== "start").length + 1;
                  return (
                    // eslint-disable-next-line react/no-array-index-key -- route waypoints are positional within a fixed plan; idx is the stop number
                    <span key={`wp-${wp.type}-${i}-${wp.wo_number || wp.name || ''}`} className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${wp.type === "start" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                      {wp.type === "start" ? "Start" : `${stopNum}. ${wp.day_label ? `(${wp.day_label}) ` : ""}${wp.wo_number || wp.name}`}
                      {wp.hours > 0 && wp.type !== "start" ? ` · ${wp.hours}h` : ""}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="rounded-lg overflow-hidden" style={{ height: "calc(100vh - 200px)", position: "relative", zIndex: 0 }} data-testid="map-container">
        <MapContainer center={defaultCenter} zoom={8} className="w-full h-full" scrollWheelZoom={true}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {hasMarkers && <FitBounds markers={allMarkers} />}

          {/* WO Markers */}
          {layers.work_orders && !visibleRoute && filteredWOs.map(wo => (
            <Marker key={`wo-${wo.id}`} position={[wo.lat, wo.lng]} opacity={dim(woHit(wo))}
              icon={wosWithInspections.has(wo.id) ? makeDualIcon() : makeWOIcon()}
              eventHandlers={{
                mouseover: (e) => e.target.openPopup(),
                mouseout: (e) => e.target.closePopup(),
              }}>
              <Popup>
                <div className="min-w-[200px]">
                  <div className="font-bold text-sm text-slate-800">{wo.wo_number}</div>
                  <div className="text-sm font-medium">{wo.title}</div>
                  <div className="text-xs text-slate-500">{wo.client_name}</div>
                  {wo.site_name && <div className="text-xs text-slate-400">{wo.site_name}</div>}
                  <div className="flex items-center gap-1 mt-1">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[wo.status] || "#64748b" }} />
                    <span className="text-xs">{STATUS_LABELS[wo.status] || wo.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {wo.scheduled_date && <div>Scheduled: <span className="font-medium">{wo.scheduled_date}</span></div>}
                    {wo.completion_date && <div>Completion: <span className="font-medium">{wo.completion_date}</span></div>}
                  </div>
                  {wo.site_address && <div className="text-[10px] text-slate-400 mt-1">{wo.site_address}</div>}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Contract job pins — blue ring + CONTRACT tag; share the WO layer toggle */}
          {layers.work_orders && !visibleRoute && (mapData.contract_jobs || []).filter(cj => !techFilter || techFilter === "all" || (cj.assigned_tech_ids || []).includes(techFilter)).map(cj => (
            <Marker key={`cj-${cj.id}`} position={[cj.lat, cj.lng]} icon={makeContractIcon(cj)} opacity={dim(cjHit(cj))}
              eventHandlers={{ mouseover: (e) => e.target.openPopup(), mouseout: (e) => e.target.closePopup() }}>
              <Popup>
                <div className="min-w-[200px]" data-testid={`map-popup-contract-${cj.id}`}>
                  <div className="inline-block text-[9px] font-black tracking-wide rounded px-1.5 py-0.5" style={{ backgroundColor: cj.color || "#3B82F6", color: cj.job_kind === "overhead" ? "#0f172a" : "#fff" }}>{cj.tag || "CON"}</div>
                  <div className="text-sm font-medium mt-1">{cj.title}</div>
                  <div className="text-xs text-slate-500">{cj.client_name}</div>
                  {cj.dates?.length > 0 && <div className="text-xs text-slate-500 mt-1">Scheduled: <span className="font-medium">{cj.dates.join(", ")}</span></div>}
                  {cj.site_address && <div className="text-[10px] text-slate-400 mt-1">{cj.site_address}</div>}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Route Markers (numbered) */}
          {visibleRoute?.waypoints?.map((wp, i) => {
            const stopNum = wp.type === "start" ? null : visibleRoute.waypoints.slice(0, i).filter(w => w.type !== "start").length + 1;
            return (
              // eslint-disable-next-line react/no-array-index-key -- waypoint markers are positional within a fixed route plan
              <Marker key={`route-${i}-${wp.wo_number || wp.name || wp.type}`} position={[wp.lat, wp.lng]}
                icon={wp.type === "start" ? makeStartIcon() : makeNumberedIcon("#3b82f6", stopNum)}>
                <Popup>
                  <div className="min-w-[160px]">
                    {wp.type === "start" ? (
                      <div className="font-bold text-sm text-emerald-700">Start Location</div>
                    ) : (
                      <>
                        <div className="font-bold text-sm text-blue-700">Stop {stopNum}: {wp.wo_number}</div>
                        <div className="text-sm font-medium">{wp.title}</div>
                        {wp.day_label && <div className="text-xs text-emerald-600 font-medium">{wp.day_label}</div>}
                        {wp.hours > 0 && <div className="text-xs text-slate-500">{wp.hours}h budgeted</div>}
                      </>
                    )}
                    {wp.name && <div className="text-xs text-slate-500">{wp.name}</div>}
                    {wp.address && <div className="text-[10px] text-slate-400 mt-0.5">{wp.address}</div>}
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* Route Polylines — one per day for color differentiation */}
          {visibleRoute?.route_geometries?.map((geom, idx) => (
            geom?.length > 1 && (
              // eslint-disable-next-line react/no-array-index-key -- one polyline per route day; idx is the day-color anchor
              <Polyline key={`route-line-${idx}`} positions={geom} pathOptions={{
                color: selectedRouteDay === "all" ? DAY_COLORS[idx % DAY_COLORS.length] : "#059669",
                weight: 4, opacity: 0.8, dashArray: "8 4"
              }} />
            )
          ))}


          {/* Customer Markers */}
          {layers.customers && (mapData.customers || []).map(c => (
            <Marker key={`cust-${c.site_id}`} position={[c.lat, c.lng]} icon={makeCustomerIcon()} opacity={dim(custHit(c))}
              eventHandlers={{
                mouseover: (e) => e.target.openPopup(),
                mouseout: (e) => e.target.closePopup(),
              }}>
              <Popup>
                <div className="min-w-[140px]">
                  <div className="font-bold text-sm">{c.client_name}</div>
                  <div className="text-xs text-slate-500">{c.site_name}</div>
                  <div className="text-xs text-slate-400">{c.city}, {c.state}</div>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Tech Home Address Pins (always visible when layer on — outline ring) */}
          {layers.technicians && (mapData.tech_home_pins || []).map(t => (
            <Marker key={`home-${t.id}`} position={[t.lat, t.lng]} icon={makeHomePinIcon(t.color)} opacity={dim(techHit(t))}>
              <Popup>
                <div className="min-w-[150px]">
                  <div className="font-bold text-sm flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full border-2" style={{ borderColor: t.color }} />
                    {t.name}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 font-medium uppercase tracking-wide">Home Address</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.address}</div>
                  {t.has_live_gps && (
                    <div className="mt-1.5 flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                      Live GPS active
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Tech Live GPS Pins. For technician-role users we further
              restrict to peers currently `on_job` (en_route / at_site /
              at_shop) — never idle techs, since that could expose
              their off-clock movement. The current user always sees
              themselves regardless of state. */}
          {layers.technicians && (mapData.technicians || [])
            .filter(t => !isTechView || t.on_job || t.id === user?.id || allTechs.find(at => at.id === t.id)?.email === user?.email)
            .map(t => (
            <Marker key={`live-${t.id}`} position={[t.lat, t.lng]} icon={makeLiveGpsPinIcon(t.color)} opacity={dim(techHit(t))}>
              <Popup>
                <div className="min-w-[150px]">
                  <div className="font-bold text-sm flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block" />
                    {t.name}
                  </div>
                  <div className="text-[10px] text-emerald-600 mt-0.5 font-medium uppercase tracking-wide">
                    {t.on_job ? "On a Job" : "Live GPS"}
                  </div>
                  {t.last_location_at && (
                    <div className="text-xs text-slate-400 mt-0.5">Last ping: {t.last_location_at}</div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Scheduled-but-no-signal "ghost" pins reveal a tech's planned
              site location. Hidden for technician-role peers — they only
              see live on-job movement, not where a peer is supposed to be. */}
          {layers.technicians && !isTechView && (mapData.scheduled_techs || []).map((g, i) => (
            // eslint-disable-next-line react/no-array-index-key -- ghost pins keyed by tech_id but a tech could have multiple scheduled sites
            <React.Fragment key={`scheduled-${g.tech_id}-${i}`}>
              <Marker position={[g.site_lat, g.site_lng]} icon={makeTechGhostIcon(g.color)}>
                <Popup>
                  <div className="min-w-[160px]">
                    <div className="font-bold text-sm flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full border-2" style={{ borderColor: g.color }} />
                      {g.name} · No signal
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Scheduled at {g.site_name || g.site_address}</div>
                    {g.last_location_at
                      ? <div className="text-xs text-slate-400 mt-1">Last GPS: {g.last_location_at}</div>
                      : <div className="text-xs text-slate-400 mt-1">No GPS today</div>}
                  </div>
                </Popup>
              </Marker>
              {g.last_lat && g.last_lng && (
                <Marker position={[g.last_lat, g.last_lng]} icon={makeTechStaleIcon(g.color)}>
                  <Popup>
                    <div className="min-w-[140px]">
                      <div className="font-semibold text-sm opacity-70">{g.name} · last known</div>
                      {g.last_location_at && <div className="text-xs text-slate-400 mt-1">Seen: {g.last_location_at}</div>}
                    </div>
                  </Popup>
                </Marker>
              )}
            </React.Fragment>
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-2 text-xs text-slate-500 flex-wrap">
        <span className="font-semibold text-slate-400">Legend:</span>
        <span className="flex items-center gap-1.5">
          <svg width="14" height="18" viewBox="0 0 32 42"><path d="M16 40 C16 40 30 22 30 14 A14 14 0 1 0 2 14 C2 22 16 40 16 40Z" fill="#f97316" stroke="#fff" strokeWidth="2"/><circle cx="16" cy="14" r="5" fill="#fff"/></svg>
          Work Order
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 22 22"><rect x="3" y="3" width="16" height="16" rx="3" fill="#8b5cf6" stroke="#fff" strokeWidth="2.5" transform="rotate(45 11 11)"/></svg>
          Customer Site
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 30 30"><circle cx="15" cy="15" r="12" fill="white" stroke="#22c55e" strokeWidth="2.5"/><polygon points="15,7 22,13 20,13 20,21 17,21 17,17 13,17 13,21 10,21 10,13 8,13" fill="#9333ea"/></svg>
          Tech Home
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="12" height="16" viewBox="0 0 42 52"><path d="M21 50 C21 50 36 30 36 18 A15 15 0 1 0 6 18 C6 30 21 50 21 50Z" fill="#9333ea" stroke="#22c55e" strokeWidth="2.5"/><circle cx="21" cy="18" r="6" fill="#fff"/></svg>
          Tech Live GPS
        </span>
      </div>
    </div>
  );
}
